import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const POLL_MS = 20000;
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

export function useNotifications() {
  const [d, setD] = useState({ items: [], unread: 0 });
  const seen = useRef(null);
  const apply = useCallback((data, announce) => {
    const ids = new Set(data.items.map((i) => i.id));
    if (announce && seen.current) data.items.filter((i) => i.unread && !seen.current.has(i.id)).forEach((i) => toast(i.text, { description: ago(i.created_at), duration: 10000 }));
    seen.current = ids;
    setD(data);
  }, []);
  useEffect(() => {
    const load = () => !document.hidden && api.get("/notifications").then((r) => apply(r.data, true)).catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [apply]);
  const markRead = () => d.unread && api.post("/notifications/read").then((r) => apply(r.data, false)).catch(() => {});
  return { ...d, markRead };
}

export function NotificationBell({ n, suffix = "" }) {
  return (
    <Popover onOpenChange={(o) => o && n.markRead()}>
      <PopoverTrigger asChild>
        <button className="relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label="Notifications" data-testid={`notifications-btn${suffix}`}>
          <Bell className="h-4 w-4" />
          {n.unread > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-[1.1rem] text-white" data-testid={`notifications-count${suffix}`}>{n.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid={`notifications-panel${suffix}`}>
        <div className="border-b px-4 py-2.5 text-xs font-heading font-semibold tracking-tight text-muted-foreground">Notifications</div>
        {n.items.length === 0 ? <div className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing yet. You'll see it here when someone with your shared list link adds an item.</div> : (
          <ul className="max-h-80 divide-y overflow-y-auto">{n.items.map((i) => (
            <li key={i.id} className={`px-4 py-2.5 text-sm ${i.unread ? "bg-primary/5" : ""}`} data-testid={`notification-${i.id}`}>
              <div>{i.text}</div><div className="text-xs text-muted-foreground">{ago(i.created_at)}</div>
            </li>
          ))}</ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
